import { useDuplicateAnalysis } from '@modules/analysis/hooks/useDuplicateAnalysis';
import { Direction } from '@modules/common/types';
import {
  addCellToDashboardLayout,
  getDashboardRow,
  getNewCellID,
} from '@modules/dashboard/helpers';
import { useUpsertDashboard } from '@modules/dashboard/hooks/useUpsertDashboard';
import { DashboardActionType } from '@modules/dashboard/store/dashboardActionType';
import { useDashboardStore } from '@modules/dashboard/store/useDashboardStore';
import { AnalysisUseCase, AnalysisViewFragment } from 'generated/graphql';
import { cloneDeep } from 'lodash';
import { useCallback, useRef } from 'react';
import scrollIntoView from 'scroll-into-view-if-needed';
import { ExpressionType } from 'v2/core/repository/eql/types';
import { EmptyWhereClause } from 'v2/core/services/queryBuilder/whereClause';
import { useGetUser } from 'v2/core/services/user';
import { shallow } from 'zustand/shallow';
import { ANALYSIS_WIDGET_MIN_HEIGHT } from '../constants';

interface useDuplicateDashboardAnalysisParams {
  analysis: AnalysisViewFragment;
}

export const useDuplicateDashboardAnalysis = ({
  analysis,
}: useDuplicateDashboardAnalysisParams) => {
  const { id: userID } = useGetUser();

  const { createAnalysis, isLoading, error } = useDuplicateAnalysis({
    eqlProps: {
      expressionType: ExpressionType.Records,
      whereClause: EmptyWhereClause,
    },
    metadata: analysis.metadata,
    metadataV2:
      analysis.useCase === AnalysisUseCase.Default ||
      analysis.useCase === AnalysisUseCase.Compare
        ? {
            quantify: analysis.metadataV2?.quantify,
          }
        : undefined,
  });

  const { upsertDashboard } = useUpsertDashboard();

  const dispatch = useDashboardStore(({ dispatch }) => dispatch);
  const {
    dashboardMetadata,
    dashboardEditToken,
    dashboardViewToken,
    dashboardId,
    cellMinimumHeightsByRowID,
  } = useDashboardStore(
    ({ metadata, dashboard, visual }) => ({
      dashboardMetadata: metadata,
      dashboardId: dashboard.id,
      dashboardEditToken: dashboard.editToken,
      dashboardViewToken: dashboard.editToken,
      cellMinimumHeightsByRowID: visual.cellMinimumHeightsByRowID,
    }),
    shallow,
  );

  const dependencies = {
    userID,
    analysis,
    dashboardId,
    dashboardEditToken,
    dashboardMetadata,
    dashboardViewToken,
    cellMinimumHeightsByRowID,
  };
  const dependenciesRef = useRef(dependencies);
  dependenciesRef.current = dependencies;

  const duplicateAnalysis = useCallback(
    async ({ rowID }: { rowID: string }) => {
      try {
        const analysisDashboardMetadata =
          dependenciesRef.current.dashboardMetadata.analysesMetadata.find(
            (metadata) => metadata.id === dependenciesRef.current.analysis.id,
          );

        const clonedLayout = cloneDeep(
          dependenciesRef.current.dashboardMetadata.layout,
        );

        const cell = {
          id: getNewCellID(),
          columnSpan: 12,
          contentID: Date.now().toString(),
        };

        const { destinationRowID } = addCellToDashboardLayout({
          cell,
          rowID,
          cellInsertDirection: Direction.Right,
          layout: clonedLayout,
        });

        dispatch({
          type: DashboardActionType.SET_METADATA,
          payload: { layout: clonedLayout },
        });

        dispatch({
          type: DashboardActionType.SET_VISUAL,
          payload: {
            cellMinimumHeightsByRowID: {
              ...dependenciesRef.current.cellMinimumHeightsByRowID,
              [destinationRowID]: {
                ...dependenciesRef.current.cellMinimumHeightsByRowID[
                  destinationRowID
                ],
                [cell.id]: ANALYSIS_WIDGET_MIN_HEIGHT,
              },
            },
          },
        });

        const resp = await createAnalysis({
          variables: {
            tenantID: dependenciesRef.current.analysis.tenantID,
            title: `Copy of ${dependenciesRef.current.analysis.title}`,
            scope: dependenciesRef.current.analysis.scope,
            panels: [],
            metadata: dependenciesRef.current.analysis.metadata,
            analysisExpresssion:
              dependenciesRef.current.analysis.context.eql?.eql ?? '',
            createdBy: dependenciesRef.current.userID,
            useCase: dependenciesRef.current.analysis.useCase,
          },
        });

        if (typeof resp === 'function' || !resp?.data?.createAnalysis?.id) {
          return;
        }

        const newAnalysisId = resp?.data?.createAnalysis?.id;

        const newLayout = cloneDeep(clonedLayout);
        const targetCell = getDashboardRow({
          rowID: destinationRowID,
          rows: newLayout.rows,
        })?.cells.find(({ id }) => id === cell.id);

        if (targetCell) {
          targetCell.contentID = newAnalysisId;

          dispatch({
            type: DashboardActionType.SET_METADATA,
            payload: { layout: newLayout },
          });
        }

        await upsertDashboard({
          dashboardProps: {
            addAnalysisIDs: [newAnalysisId],
            metadataV2: {
              layout: newLayout,
              analysesMetadata: [
                ...dependenciesRef.current.dashboardMetadata.analysesMetadata,
                { ...analysisDashboardMetadata, id: newAnalysisId },
              ],
            },
          },
          shouldRefetchAnalyses: true,
          shouldRefetchDashboard: true,
        });

        const cellElement = document.getElementById(cell.id);
        if (cellElement) {
          scrollIntoView(cellElement, {
            scrollMode: 'if-needed',
            block: 'nearest',
            inline: 'nearest',
            behavior: 'smooth',
          });
        }
      } catch {}
    },
    [dispatch, createAnalysis, upsertDashboard],
  );

  return {
    duplicateAnalysis,
    isLoading,
    error,
  };
};
